import { readAIStream } from "./aiStreamEvents";
import { getAuthHeaders } from './auth';
import { isAINotice, requestAcknowledgement, reportCapabilityOutcome } from './aiReliability';
import type { CapabilityOutcome } from '@shared/ai-capabilities';

const API_URL = import.meta.env.VITE_API_URL || '';

// Tracked so callers outside this module (the stale-deploy auto-reload
// handler in main.tsx) can tell whether an AI response is mid-stream before
// yanking the tab out from under it with a hard reload — that's exactly what
// was silently discarding in-progress chat answers.
let activeStreamCount = 0;
export function isStreamActive(): boolean {
    return activeStreamCount > 0;
}

export interface StreamChunk {
    type: 'token' | 'chunk' | 'done' | 'error';
    token?: string;
    content?: string;
    index?: number;
    model?: string;
    message?: string;
    summary_id?: string;
    studySetId?: string;
    finalSummary?: string;
    capability?: CapabilityOutcome | null;
}

export interface StreamOptions {
    onToken?: (token: string) => void;
    onChunk?: (chunk: { index: number; content: string; model?: string }) => void;
    onDone?: (data: { content?: string; summary_id?: string; studySetId?: string; finalSummary?: string; capability?: CapabilityOutcome | null }) => void;
    onError?: (error: string) => void;
    token?: string;
    body?: Record<string, unknown>;
    signal?: AbortSignal;
    /**
     * Abort if no bytes arrive for this long (reset on every chunk, not a
     * hard cap on total duration — a real response can legitimately stream
     * for minutes). Without this, a stalled connection (e.g. a large image
     * attachment overwhelming the vision model) hangs forever with the
     * caller's loading state never clearing.
     */
    timeoutMs?: number;
}

/**
 * Robust utility for consuming AI Server-Sent Events (SSE)
 */
export async function consumeAIStream(endpoint: string, options: StreamOptions): Promise<void> {
    const controller = new AbortController();
    if (options.signal) {
        if (options.signal.aborted) controller.abort();
        else options.signal.addEventListener('abort', () => controller.abort(), { once: true });
    }

    let timedOut = false;
    let inactivityTimer: ReturnType<typeof setTimeout> | undefined;
    const resetInactivityTimer = () => {
        if (!options.timeoutMs) return;
        if (inactivityTimer) clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, options.timeoutMs);
    };

    activeStreamCount += 1;
    try {
        resetInactivityTimer();
        const authHeaders = await getAuthHeaders();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            ...authHeaders,
        };

        if (options.token) {
            headers['Authorization'] = `Bearer ${options.token}`;
        }

        const url = endpoint.startsWith('http') ? endpoint : `${API_URL}${endpoint}`;
        const response = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(options.body || {}),
            signal: controller.signal,
        });
        resetInactivityTimer();

        if (!response.ok) {
            let errorMsg = `Server error: ${response.status}`;
            try {
                const errData = await response.json();
                errorMsg = errData.error || errorMsg;
            } catch (e) { /* ignore */ }
            throw new Error(errorMsg);
        }

        // A plain JSON reply instead of a stream: the server wants a one-time
        // acknowledgement before answering complex maths with Standard AI.
        // Ask once (app-level dialog), then resend the same request.
        if ((response.headers.get('content-type') || '').includes('application/json')) {
            const payload = await response.json().catch(() => null);
            if (isAINotice(payload) && payload.requiresAcknowledgement && !options.body?.acknowledgedNotice) {
                // The student may take a while to read this — not a stalled stream.
                if (inactivityTimer) clearTimeout(inactivityTimer);
                const accepted = await requestAcknowledgement(payload);
                if (!accepted) {
                    options.onError?.("Message not sent. You can ask again any time.");
                    return;
                }
                return await consumeAIStream(endpoint, { ...options, body: { ...(options.body || {}), acknowledgedNotice: payload.key } });
            }
            throw new Error((payload as { error?: string } | null)?.error || "Unexpected response from the server");
        }

        if (!response.body) throw new Error("Response body is not readable");
        const finished = await readAIStream(response.body, async value => {
            const data = value as StreamChunk;
            if (!data || typeof data !== 'object') return false;
            switch (data.type) {
                case 'token': if (typeof data.token === 'string') options.onToken?.(data.token); return false;
                case 'chunk': if (data.content !== undefined && data.index !== undefined) options.onChunk?.({ index: data.index, content: data.content, model: data.model }); return false;
                case 'done':
                    reportCapabilityOutcome(data.capability);
                    await options.onDone?.({ content: data.content || data.token || data.finalSummary, summary_id: data.summary_id || data.studySetId, studySetId: data.studySetId, finalSummary: data.finalSummary, capability: data.capability ?? null });
                    return true;
                case 'error': throw new Error(data.message || 'Novari could not finish the response. Please try again.');
                default: return false;
            }
        }, resetInactivityTimer);
        if (!finished) throw new Error('The connection ended before Novari finished answering. Please try again.');
    } catch (e) {
        if (e instanceof Error && e.name === 'AbortError') {
            if (timedOut) {
                const timeoutMsg = "The AI took too long to respond. Please try again.";
                if (options.onError) options.onError(timeoutMsg);
                else throw new Error(timeoutMsg, { cause: e });
                return;
            }
            return; // Ignore intentional aborts (e.g. navigating away)
        }
        if (options.onError) options.onError(e instanceof Error ? e.message : String(e));
        else throw e;
    } finally {
        if (inactivityTimer) clearTimeout(inactivityTimer);
        activeStreamCount -= 1;
    }
}
